import React, { useEffect, useMemo, useState } from "react";
import {
  Typography,
  Button,
  Box,
  Paper,
  Stack,
  alpha,
  useTheme,
  Chip,
  CircularProgress,
  Alert,
} from "@mui/material";
import {
  History as HistoryIcon,
  CalendarMonth,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import { useShipSettings } from "../../Contexts/ShipSettingsContext";
import { useVoyageContextV2 } from "../../Contexts/VoyageContextV2";

const CompletedVoyages = () => {
  const theme = useTheme();
  const { shipData } = useShipSettings();
  const {
    currentVoyage,
    shipVoyages,
    isFetchingCloudVoyages,
    fetchVoyagesForShip,
    fetchFromCloud,
  } = useVoyageContextV2();
  const [loadingVoyageId, setLoadingVoyageId] = useState(null);

  useEffect(() => {
    if (shipData?.id) {
      fetchVoyagesForShip();
    }
  }, [fetchVoyagesForShip, shipData?.id]);

  const completedVoyages = useMemo(
    () => (shipVoyages || []).filter((voyage) => voyage.status === "completed"),
    [shipVoyages]
  );

  const formatDateTime = (value) => {
    if (!value) return "N/A";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "N/A";
    return date.toLocaleString();
  };

  const handleLoadVoyage = async (voyageId) => {
    if (!voyageId) return;
    setLoadingVoyageId(voyageId);
    try {
      await fetchFromCloud(voyageId);
    } finally {
      setLoadingVoyageId(null);
    }
  };

  if (!shipData?.id) {
    return (
      <Box sx={{ maxWidth: 1000, mx: "auto", pb: 6, px: 2 }}>
        <Alert severity="info">Select a ship to view completed voyages.</Alert>
      </Box>
    );
  }

  if (isFetchingCloudVoyages) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 10 }}>
        <CircularProgress thickness={2} size={52} />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1000, mx: "auto", pb: 10, px: 2 }}>
      <Box sx={{ mb: 6, mt: 2 }}>
        <Typography variant="h3" fontWeight="900" sx={{ letterSpacing: -1, mb: 1 }}>
          Voyage Archive
        </Typography>
        <Typography variant="body1" color="textSecondary" sx={{ fontSize: "1.1rem" }}>
          Completed voyages for <strong>{shipData?.name || shipData?.id}</strong>.
        </Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          sx={{ mt: 2 }}
          onClick={() => fetchVoyagesForShip()}
          disabled={isFetchingCloudVoyages}
        >
          Refresh
        </Button>
      </Box>

      <Stack spacing={2.5}>
        {completedVoyages.length === 0 ? (
          <Paper
            variant="outlined"
            sx={{
              p: 8,
              textAlign: "center",
              borderRadius: 4,
              borderStyle: "dashed",
              bgcolor: "transparent",
            }}
          >
            <HistoryIcon
              sx={{ fontSize: 48, color: "text.disabled", mb: 2, opacity: 0.3 }}
            />
            <Typography variant="h6" color="textSecondary">
              No completed voyages found.
            </Typography>
          </Paper>
        ) : (
          completedVoyages.map((voyage) => (
            <Paper
              key={voyage.voyageId}
              elevation={0}
              sx={{
                p: 0,
                borderRadius: 4,
                border: "1px solid",
                borderColor: alpha(theme.palette.divider, 0.08),
                bgcolor: "background.paper",
                position: "relative",
                overflow: "hidden",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                boxShadow: `0 4px 20px -5px ${alpha(
                  theme.palette.common.black,
                  0.05
                )}`,
                "&:hover": {
                  borderColor: theme.palette.primary.main,
                  boxShadow: `0 12px 30px -10px ${alpha(
                    theme.palette.primary.main,
                    0.15
                  )}`,
                  transform: "translateY(-4px)",
                  "& .voyage-accent": { width: "8px" },
                },
              }}
            >
              <Box
                className="voyage-accent"
                sx={{
                  position: "absolute",
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: "4px",
                  bgcolor: theme.palette.primary.main,
                  transition: "0.3s",
                }}
              />

              <Box sx={{ p: 3, pl: 4 }}>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  justifyContent="space-between"
                  alignItems={{ xs: "flex-start", sm: "center" }}
                  spacing={2}
                >
                  <Box>
                    <Typography
                      variant="caption"
                      fontWeight="900"
                      color="text.disabled"
                      sx={{ letterSpacing: 1.5, display: "block", mb: 0.5 }}
                    >
                      VOYAGE
                    </Typography>
                    <Typography variant="h4" fontWeight="900" color="text.primary">
                      #{voyage.voyageNumber}
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                      <CalendarMonth sx={{ fontSize: 18, color: "primary.main" }} />
                      <Typography variant="caption" fontWeight="700" color="text.secondary">
                        Ended: {formatDateTime(voyage.endedAt)}
                      </Typography>
                    </Stack>
                    <Typography variant="caption" color="text.secondary">
                      Updated: {formatDateTime(voyage.updatedAt || voyage.createdAt)}
                    </Typography>
                  </Box>

                  <Stack direction="row" spacing={1} alignItems="center">
                    <Chip label={voyage.status} size="small" color="default" variant="outlined" />
                    <Button
                      variant="contained"
                      startIcon={
                        loadingVoyageId === voyage.voyageId ? (
                          <CircularProgress size={14} color="inherit" />
                        ) : (
                          <DownloadIcon />
                        )
                      }
                      disabled={loadingVoyageId === voyage.voyageId}
                      onClick={() => handleLoadVoyage(voyage.voyageId)}
                      sx={{ fontWeight: 800, borderRadius: 2 }}
                    >
                      {currentVoyage?.voyageId === voyage.voyageId ? "Loaded" : "Load"}
                    </Button>
                  </Stack>
                </Stack>
              </Box>
            </Paper>
          ))
        )}
      </Stack>
    </Box>
  );
};

export default CompletedVoyages;
